// pages/News.jsx
import { useState, useEffect } from "react";
import { T } from "../Tokens";
import { API_BASE } from "../config";
import { Skeleton } from "../components/UI";

// ── constants ─────────────────────────────────────────────────────
const PAGE_SIZE = 18;

const TOPICS = [
  "All",
  "Technology",
  "Sports",
  "Space",
  "Politics",
  "Health",
  "Business",
  "Science",
  "Entertainment",
  "World",
];

const TOPIC_KEYWORDS = {
  Technology: [
    "tech",
    "ai",
    "software",
    "app",
    "cyber",
    "robot",
    "data",
    "digital",
    "computer",
    "internet",
    "chip",
    "startup",
    "electric",
    "device",
  ],
  Sports: [
    "sport",
    "football",
    "cricket",
    "tennis",
    "olympic",
    "match",
    "tournament",
    "league",
    "fifa",
    "nba",
    "ipl",
    "player",
    "coach",
    "team",
  ],
  Space: [
    "space",
    "nasa",
    "rocket",
    "satellite",
    "mars",
    "moon",
    "orbit",
    "asteroid",
    "telescope",
    "galaxy",
    "cosmos",
    "launch",
    "iss",
  ],
  Politics: [
    "election",
    "president",
    "minister",
    "government",
    "parliament",
    "senate",
    "vote",
    "policy",
    "congress",
    "bilateral",
    "treaty",
    "sanction",
  ],
  Health: [
    "health",
    "covid",
    "vaccine",
    "hospital",
    "disease",
    "cancer",
    "drug",
    "surgery",
    "mental",
    "therapy",
    "virus",
    "epidemic",
    "fda",
    "who",
  ],
  Business: [
    "market",
    "stock",
    "economy",
    "trade",
    "gdp",
    "inflation",
    "bank",
    "investment",
    "startup",
    "revenue",
    "profit",
    "merger",
    "ipo",
  ],
  Science: [
    "science",
    "research",
    "study",
    "climate",
    "energy",
    "physics",
    "biology",
    "chemistry",
    "genome",
    "fossil",
    "discovery",
  ],
  Entertainment: [
    "movie",
    "film",
    "music",
    "celebrity",
    "award",
    "netflix",
    "actor",
    "singer",
    "concert",
    "album",
    "box office",
    "oscar",
  ],
  World: [
    "war",
    "conflict",
    "ukraine",
    "israel",
    "china",
    "russia",
    "india",
    "europe",
    "africa",
    "un",
    "nato",
    "refugee",
    "crisis",
  ],
};

// ── helpers ───────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function relTime(iso) {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── NewsCard ──────────────────────────────────────────────────────
function NewsCard({ article, index }) {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? T.bg3 : T.bg2,
        border: `1px solid ${hovered ? T.borderHover : T.border}`,
        borderRadius: T.radiusLg,
        overflow: "hidden",
        transition: "all 0.18s",
        animation: `fadeUp 0.2s ease ${(index % 10) * 0.03}s both`,
        display: "flex",
        flexDirection: "column",
        transform: hovered ? "translateY(-2px)" : "none",
        boxShadow: hovered ? "0 6px 24px #00000060" : "none",
        height: "100%",
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: "100%",
          height: 140,
          flexShrink: 0,
          background: T.bg3,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {article.urlToImage && !imgError ? (
          <img
            src={article.urlToImage}
            alt=""
            onError={() => setImgError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.3s",
              transform: hovered ? "scale(1.04)" : "scale(1)",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill={T.bg4}>
              <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
            </svg>
          </div>
        )}
        <div style={{ position: "absolute", top: 8, left: 8 }}>
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              padding: "2px 7px",
              borderRadius: T.radiusSm,
              letterSpacing: "0.06em",
              background: "#000000cc",
              border: `1px solid ${T.greenBorder}`,
              color: T.greenText,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              backdropFilter: "blur(4px)",
            }}
          >
            {article.sourceName || "Unknown"}
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          padding: "12px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          flex: 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {article.author && (
            <span
              style={{
                fontSize: 10,
                color: T.textDim,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {article.author}
            </span>
          )}
          <span
            style={{
              fontSize: 9,
              color: T.textDim,
              whiteSpace: "nowrap",
              marginLeft: "auto",
              flexShrink: 0,
            }}
          >
            {relTime(article.publishedAt)}
          </span>
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: T.textPrimary,
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.title}
        </div>

        <div
          style={{
            fontSize: 11,
            color: T.textSecondary,
            lineHeight: 1.6,
            flex: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.description || ""}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: 8,
            marginTop: "auto",
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <span style={{ fontSize: 9, color: T.textDim }}>
            {fmtDate(article.publishedAt)}
          </span>
          {article.hash && (
            <span
              style={{ fontSize: 9, color: T.textDim, fontFamily: T.fontMono }}
            >
              #{article.hash.slice(0, 8)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── SkeletonCard ──────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div
      style={{
        background: T.bg2,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusLg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Skeleton w="100%" h={140} style={{ borderRadius: 0 }} />
      <div
        style={{
          padding: "12px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <Skeleton w="50%" h={10} />
          <Skeleton w="25%" h={10} style={{ marginLeft: "auto" }} />
        </div>
        <Skeleton w="100%" h={13} />
        <Skeleton w="85%" h={13} />
        <Skeleton w="60%" h={13} />
        <Skeleton w="100%" h={10} />
        <Skeleton w="70%" h={10} />
      </div>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────
function Pagination({ page, hasMore, loading, onPrev, onNext }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        background: T.bg1,
        borderTop: `1px solid ${T.border}`,
        flexShrink: 0,
      }}
    >
      <button
        onClick={onPrev}
        disabled={page === 0 || loading}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          background: page === 0 ? T.bg2 : T.bg3,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          color: page === 0 ? T.textDim : T.textSecondary,
          fontSize: 11,
          cursor: page === 0 ? "not-allowed" : "pointer",
          fontFamily: T.fontMono,
          opacity: page === 0 ? 0.4 : 1,
        }}
      >
        ← Prev
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 10, color: T.textDim }}>Page</span>
        <span
          style={{
            fontSize: 11,
            color: T.textPrimary,
            background: T.bg3,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm,
            padding: "2px 10px",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {page + 1}
        </span>
        <span style={{ fontSize: 10, color: T.textDim }}>
          · {PAGE_SIZE} per page
        </span>
      </div>

      <button
        onClick={onNext}
        disabled={!hasMore || loading}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          background: !hasMore ? T.bg2 : T.greenDim,
          border: `1px solid ${!hasMore ? T.border : T.greenBorder}`,
          borderRadius: T.radius,
          color: !hasMore ? T.textDim : T.greenText,
          fontSize: 11,
          cursor: !hasMore ? "not-allowed" : "pointer",
          fontFamily: T.fontMono,
          opacity: !hasMore ? 0.4 : 1,
        }}
      >
        Next →
      </button>
    </div>
  );
}

// ── News Page ─────────────────────────────────────────────────────
export default function News() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [topic, setTopic] = useState("All");
  const [topicOpen, setTopicOpen] = useState(false);

  const [search, setSearch] = useState(
    () => localStorage.getItem("news_search") || "",
  );
  const [sortBy, setSortBy] = useState(
    () => localStorage.getItem("news_sortBy") || "newest",
  );
  const [page, setPage] = useState(
    () => Number(localStorage.getItem("news_page")) || 0,
  );

  useEffect(() => {
    localStorage.setItem("news_search", search);
    localStorage.setItem("news_sortBy", sortBy);
    localStorage.setItem("news_page", page);
  }, [search, sortBy, page]);

  const fetchPage = (p) => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/news?page=${p}&size=${PAGE_SIZE}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setArticles(data);
        setHasMore(data.length === PAGE_SIZE);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPage(page);
  }, []);

  const goToPage = (p) => {
    setPage(p);
    fetchPage(p);
    document
      .getElementById("news-list")
      ?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filtered = articles
    .filter((a) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        a.title?.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q) ||
        a.sourceName?.toLowerCase().includes(q) ||
        a.author?.toLowerCase().includes(q)
      );
    })
    .filter((a) => {
      if (topic === "All") return true;
      const keywords = TOPIC_KEYWORDS[topic] || [];
      const text =
        `${a.title || ""} ${a.description || ""} ${a.content || ""}`.toLowerCase();
      return keywords.some((kw) => text.includes(kw));
    })
    .sort((a, b) => {
      if (sortBy === "newest")
        return new Date(b.publishedAt) - new Date(a.publishedAt);
      if (sortBy === "oldest")
        return new Date(a.publishedAt) - new Date(b.publishedAt);
      if (sortBy === "source")
        return (a.sourceName || "").localeCompare(b.sourceName || "");
      return 0;
    });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: T.bg0,
      }}
    >
      {/* Page header */}
      <div
        style={{
          background: T.bg1,
          borderBottom: `1px solid ${T.border}`,
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: T.radius,
            background: T.blueDim,
            border: `1px solid ${T.blueBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill={T.blue}>
            <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.textPrimary }}>
            News Feed
          </div>
          <div style={{ fontSize: 10, color: T.textDim }}>
            {loading
              ? "loading…"
              : `${articles.length} articles · page ${page + 1}`}
          </div>
        </div>
        {!loading && !error && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            {[...new Set(articles.map((a) => a.sourceName).filter(Boolean))]
              .slice(0, 3)
              .map((src) => (
                <span
                  key={src}
                  className="hide-mobile"
                  style={{
                    fontSize: 9,
                    padding: "2px 7px",
                    borderRadius: T.radiusSm,
                    background: T.bg3,
                    border: `1px solid ${T.border}`,
                    color: T.textDim,
                  }}
                >
                  {src}
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Search + Sort */}
      <div
        style={{
          background: T.bg1,
          borderBottom: `1px solid ${T.border}`,
          padding: "10px 16px",
          display: "flex",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            background: T.bg2,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: "8px 11px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill={T.textDim}>
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search within this page…"
            style={{
              flex: 1,
              background: "none",
              border: "none",
              outline: "none",
              fontSize: 11,
              color: T.textPrimary,
              fontFamily: T.fontMono,
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                background: "none",
                border: "none",
                color: T.textDim,
                cursor: "pointer",
                fontSize: 14,
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
        <select
          className="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="source">By source</option>
        </select>
      </div>

      {/* Topic filter */}
      <div
        style={{
          background: T.bg1,
          borderBottom: `1px solid ${T.border}`,
          padding: "8px 16px",
          flexShrink: 0,
          position: "relative",
        }}
      >
        {/* Desktop — scrollable pills */}
        <div
          className="topic-pills"
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            scrollbarWidth: "none",
          }}
        >
          {TOPICS.map((t) => (
            <button
              key={t}
              onClick={() => setTopic(t)}
              style={{
                padding: "5px 12px",
                borderRadius: 20,
                flexShrink: 0,
                border: `1px solid ${topic === t ? T.greenBorder : T.border}`,
                background: topic === t ? T.greenDim : T.bg2,
                color: topic === t ? T.greenText : T.textDim,
                fontSize: 11,
                cursor: "pointer",
                fontFamily: T.fontMono,
                whiteSpace: "nowrap",
                transition: "all 0.15s",
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Mobile — dropdown trigger */}
        <button
          className="topic-mobile-btn"
          onClick={() => setTopicOpen((o) => !o)}
          style={{
            display: "none",
            width: "100%",
            padding: "8px 12px",
            background: T.bg2,
            border: `1px solid ${topic !== "All" ? T.greenBorder : T.border}`,
            borderRadius: T.radius,
            color: topic !== "All" ? T.greenText : T.textSecondary,
            fontSize: 11,
            cursor: "pointer",
            fontFamily: T.fontMono,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            Topic: <strong>{topic}</strong>
          </span>
          <span style={{ fontSize: 9, color: T.textDim }}>
            {topicOpen ? "▲" : "▼"}
          </span>
        </button>

        {/* Mobile — dropdown menu */}
        {topicOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: T.bg1,
              border: `1px solid ${T.border}`,
              borderTop: "none",
              zIndex: 100,
              display: "flex",
              flexDirection: "column",
              maxHeight: 280,
              overflowY: "auto",
              boxShadow: "0 8px 24px #00000060",
            }}
          >
            {TOPICS.map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTopic(t);
                  setTopicOpen(false);
                }}
                style={{
                  padding: "13px 16px",
                  background: topic === t ? T.greenDim : "none",
                  border: "none",
                  borderBottom: `1px solid ${T.border}`,
                  color: topic === t ? T.greenText : T.textSecondary,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: T.fontMono,
                  textAlign: "left",
                  transition: "background 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span style={{ width: 14, color: T.green }}>
                  {topic === t ? "✓" : ""}
                </span>
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Article list */}
      <div
        id="news-list"
        style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}
      >
        {loading && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              background: T.redDim,
              border: `1px solid ${T.redBorder}`,
              borderRadius: T.radius,
              padding: "14px 16px",
              fontSize: 11,
              color: T.redText,
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Failed to load articles
            </div>
            <div style={{ color: "#b06060", marginBottom: 6 }}>{error}</div>
            <div style={{ marginTop: 10 }}>
              <button
                onClick={() => fetchPage(page)}
                style={{
                  padding: "6px 12px",
                  background: T.bg3,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radius,
                  color: T.textSecondary,
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: T.fontMono,
                }}
              >
                ↺ Retry
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: T.textDim,
              fontSize: 12,
              paddingTop: 60,
            }}
          >
            {search
              ? `No articles matching "${search}" on this page`
              : topic !== "All"
                ? `No ${topic} articles on this page`
                : "No articles found"}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {filtered.map((article, i) => (
              <a
                key={article._id || i}
                href={article.url}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none" }}
              >
                <NewsCard article={article} index={i} />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        page={page}
        hasMore={hasMore}
        loading={loading}
        onPrev={() => goToPage(page - 1)}
        onNext={() => goToPage(page + 1)}
      />
    </div>
  );
}
